import { z } from "zod";

// ---- Server-driven widget config (mirrors the API's /v1/config response) ----

export const themeTokensSchema = z
  .object({
    colorScheme: z.enum(["light", "dark", "system"]).optional(),
    colors: z.record(z.string(), z.string().optional()).optional(),
    radius: z.number().optional(),
    typography: z
      .record(z.string(), z.union([z.string(), z.number()]).optional())
      .optional(),
  })
  .nullable();
export type ThemeTokens = z.infer<typeof themeTokensSchema>;

export const activationConfigSchema = z.object({
  fab: z.boolean(),
  shake: z.boolean(),
  programmatic: z.boolean(),
});
export type ActivationConfig = z.infer<typeof activationConfigSchema>;

export const widgetConfigSchema = z.object({
  theme: themeTokensSchema,
  enabledModules: z.object({
    messages: z.boolean(),
    featureRequests: z.boolean(),
    changelog: z.boolean(),
  }),
  copy: z.record(z.string(), z.string().optional()).nullable(),
  activation: activationConfigSchema,
  autoShow: z
    .object({
      announcements: z.boolean(),
      changelog: z.boolean(),
      messages: z.boolean().optional(),
    })
    .optional(),
});
export type WidgetConfig = z.infer<typeof widgetConfigSchema>;

export interface AutoShowConfig {
  announcements: boolean;
  changelog: boolean;
  /** Auto-open the panel to a thread when there's a new unread admin reply. */
  messages: boolean;
}

// ---- Identity ----

export interface WherdUser {
  /** Your stable user id (becomes externalId server-side). */
  id: string;
  email?: string;
  name?: string;
  /** HMAC-SHA256(id, secretKey) computed on your server — marks identity verified. */
  userHash?: string;
  traits?: Record<string, unknown>;
}

// ---- Views the widget can show ----

export type WherdView = "messages" | "changelog" | "featureRequests";

/** Message category shown as chips when starting a new thread. */
export type MessageType = "idea" | "issue" | "other";

// ---- API response shapes ----

export interface IdentifyResponse {
  endUserId: string;
  verified: boolean;
}

export interface StartMessageResponse {
  id: string;
  createdAt: string;
}

// ---- Messages (two-way threads) ----

export type MessageAuthor = "user" | "admin";

export interface MessageReply {
  author: MessageAuthor;
  body: string;
  createdAt: string;
}

export interface MessageThread {
  id: string;
  /** Who opened the thread. Defaults to "user"; "admin" means an operator
   * reached out first, so the opening body renders as their message. */
  origin?: MessageAuthor;
  body: string;
  createdAt: string;
  /** Admin replies the end-user hasn't read yet. */
  unread: number;
  replies: MessageReply[];
}

export interface MessagesResponse {
  threads: MessageThread[];
  unreadTotal: number;
}

// ---- Announcements (broadcasts) ----

export type AnnouncementSeverity = "info" | "warning" | "critical";

export interface Announcement {
  id: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  publishedAt: string | null;
}

export interface AnnouncementsResponse {
  announcements: Announcement[];
}

export interface ChangelogEntry {
  id: string;
  title: string;
  body: string;
  publishedAt: string | null;
  read: boolean;
}

export interface ChangelogResponse {
  entries: ChangelogEntry[];
  unreadCount: number;
}

export type FeatureRequestStatus = "open" | "planned" | "in_progress" | "done";

export interface FeatureRequest {
  id: string;
  title: string;
  body: string | null;
  status: FeatureRequestStatus;
  voteCount: number;
  createdAt: string;
  hasVoted: boolean;
}
